import { Component, DoCheck, OnDestroy, OnInit } from '@angular/core';
import { storageUtil } from 'src/app/utils';
import { Router } from '@angular/router';
import { SelectOption } from '../ui/select/select.component';
import { BrowserService, CryptoService } from 'src/app/core';
import {
  COLLEGE_RECON_CONSTANTS,
  ADMIN_USERS,
  storageConstant,
} from '../../../constants';
import { MenuItem } from 'primeng/api';
import { DataService } from 'src/app/services/data-link.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'header',
  templateUrl: './header.component.html',
})
export class HeaderComponent implements DoCheck, OnInit, OnDestroy {
  user: any;
  emptyItems = [];
  items: MenuItem[] = [];
  loginInfoData: any[] = [];
  userName = '';
  profileOptions: SelectOption[] = [];
  selectedOption!: SelectOption;
  isBrowser: any;
  pathname: string = '';
  private loginStatusSubscription?: Subscription;
  private veteranInfoSubscription?: Subscription;

  constructor(
    private _storageUtil: storageUtil,
    private _cryptoService: CryptoService,
    private router: Router,
    private _browserService: BrowserService,
    private _dataLinkService: DataService,
  ) {}

  ngDoCheck() {
    this.isBrowser = this._browserService.checkBrowser();
    if (this.isBrowser) {
      const siteSrc = window.location.href;
      const urlData = siteSrc.split('/');
      const mediaPath = urlData[3].split('?');
      if (urlData[4] && urlData[4] == 'bah') {
        this.pathname = 'media';
      } else if (urlData[4] && urlData[4].includes('pay-calculator')) {
        this.pathname = 'pay-calculator';
      } else {
        this.pathname = mediaPath[0];
      }
    }
  }

  ngOnInit() {
    this.isBrowser = this._browserService.checkBrowser();
    // Always load user data from storage on init (for page refresh scenarios)
    this.loadUserData();
    this.setMenuItems();

    // Subscribe to login status changes
    this.loginStatusSubscription = this._dataLinkService.currentLoginStatus$.subscribe(
      () => {
        // Always check storage when login status changes
        // This handles both login and logout scenarios
        this.loadUserData();
        this.setMenuItems();
      },
    );

    this.veteranInfoSubscription =
      this._dataLinkService.currentVeteranInfo$.subscribe((veteranInfo) => {
        this.syncUserNameFromVeteranInfo(veteranInfo);
      });
  }

  ngOnDestroy() {
    // Unsubscribe to prevent memory leaks
    this.loginStatusSubscription?.unsubscribe();
    this.veteranInfoSubscription?.unsubscribe();
  }

  /** Keeps header menu name in sync after profile saves (via DataService veteran info). */
  private syncUserNameFromVeteranInfo(veteranInfo: any): void {
    if (!this.isBrowser || !veteranInfo || this.user?.type !== 'user') {
      return;
    }
    const nameFromParts = [
      veteranInfo.firstName,
      veteranInfo.middleName,
      veteranInfo.lastName,
    ]
      .filter((part: string) => part)
      .join(' ')
      .trim();
    const name = nameFromParts || veteranInfo.name;
    if (!name || this.user?.name === name) {
      return;
    }
    this.user = { ...this.user, name };
    const encryptedData = this._storageUtil.getItem(
      storageConstant.LOGGED_IN_USER,
    );
    if (encryptedData) {
      try {
        const decrypted = this._cryptoService.decryptData(
          encryptedData,
          COLLEGE_RECON_CONSTANTS.userEncryptDecryptSecretKey,
        );
        const storedUser = decrypted ? JSON.parse(decrypted) : null;
        if (storedUser) {
          storedUser.name = name;
          this._storageUtil.setItem(
            this._cryptoService.encryptData(
              JSON.stringify(storedUser),
              COLLEGE_RECON_CONSTANTS.userEncryptDecryptSecretKey,
            ),
            storageConstant.LOGGED_IN_USER,
          );
        }
      } catch (error) {
        console.error('Error updating logged-in user name:', error);
      }
    }
    this.setMenuItems();
  }

  loadUserData() {
    if (this.isBrowser) {
      const encryptedData = this._storageUtil.getItem('loggedInUser');
      if (encryptedData) {
        try {
          const decryptedData = this._cryptoService.decryptData(
            encryptedData,
            COLLEGE_RECON_CONSTANTS.userEncryptDecryptSecretKey,
          );
          this.user = decryptedData ? JSON.parse(decryptedData) : null;
        } catch (error) {
          console.error('Error parsing user data:', error);
          this.user = null;
        }
      } else {
        this.user = null;
      }
    }
  }

  setMenuItems() {
    this.items = [
      {
        label: 'Profile',
        icon: 'pi pi-user',
        routerLink: ['/student/profile'],
        visible: this.user && this.user.type === 'user' ? true : false,
      },
      {
        label: 'Inbox',
        icon: 'pi pi-inbox',
        routerLink: ['/student/inbox'],
        visible: this.user && this.user.type === 'user' ? true : false,
      },
      {
        label: 'Explore',
        icon: 'pi pi-compass',
        items: [
          {
            label: 'GI BILL',
            command: () => {
              this.isBrowser &&
                window.open(
                  'https://collegerecon.com/gi-bill/',
                  '_blank',
                  'noopener,noreferrer',
                );
            },
          },
          {
            label: 'Military Friendly Colleges',
            command: () => {
              this.isBrowser &&
                window.open(
                  'https://collegerecon.com/military-friendly-colleges/',
                  '_blank',
                  'noopener,noreferrer',
                );
            },
          },
          {
            label: 'Scholarships for Veterans',
            command: () => {
              this.isBrowser &&
                window.open(
                  'https://collegerecon.com/military-veteran-scholarships/',
                  '_blank',
                  'noopener,noreferrer',
                );
            },
          },
          {
            label: 'Degrees for Military',
            command: () => {
              this.isBrowser &&
                window.open(
                  'https://collegerecon.com/degrees-for-military/',
                  '_blank',
                  'noopener,noreferrer',
                );
            },
          },
          {
            label: 'Veteran Deals',
            command: () => {
              this.isBrowser &&
                window.open(
                  'https://collegerecon.com/save-time-money/',
                  '_blank',
                  'noopener,noreferrer',
                );
            },
          },
        ],
        visible: !this.user,
      },
      {
        label: 'Find School',
        icon: 'pi pi-fw pi-search',
        routerLink: ['/'],
        visible: this.user && this.user.type === 'user' ? true : false,
      },
      {
        label: 'Sign Up',
        routerLink: ['/register'],
        visible: !this.user ? true : false,
      },
      {
        label: 'Login',
        styleClass: 'menu-button',
        routerLink: ['/login'],
        visible: !this.user ? true : false,
      },
      {
        label: this.user?.name,
        icon: 'pi pi-user',
        visible: this.user ? true : false,
        items: [
          {
            label: 'Settings',
            icon: 'pi pi-cog',
            routerLink: ['/student/settings'],
            visible: this.user && this.user.type === 'user' ? true : false,
          },
          {
            label: 'College Admin Panel',
            icon: 'pi pi-book',
            routerLink: ['/college/profile/overview'],
            visible:
              this.user && ADMIN_USERS.includes(this.user.type) ? true : false,
          },
          {
            label: 'Logout',
            icon: 'pi pi-sign-out',
            command: () => this.logout(),
          },
        ],
      },
    ];
  }

  logout() {
    
    // Clear local storage
    if (this._browserService) this._storageUtil.clearStorage();
    
    // Update UI
    this.user = null;
    this.setMenuItems();
    
    // Navigate to login
    this.router.navigate([`/login`]);
  }
}
